import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client';
import {
  Button,
  Modal,
  EditIcon,
  Preloader,
  DatePicker,
  Label,
  ITabOption,
  Tabs,
  H2,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import dayjs from 'dayjs';

import { LocalizationContext } from 'localization';
import { debounce } from 'utils';

import s from './AllBookingsModal.module.scss';
import {
  BOOKINGS,
  HOST,
  IBookingsRequest,
  IBookingsResponse,
  IHostResponse,
} from './schema';

enum EBookingStatus {
  FINISHED = 'FINISHED',
  ACTIVE = 'ACTIVE',
}

interface IAllBookingsModalProps {
  hostId: number;
  editBooking: (id: number) => void;
  onClose: VoidFunction;
}
export const AllBookingsModal: FC<IAllBookingsModalProps> = ({
  hostId,
  editBooking,
  onClose,
}) => {
  const { t, lang } = useContext(LocalizationContext);

  const TABS: Array<ITabOption<EBookingStatus>> = [
    { title: t('booking.list.tabs.active'), value: EBookingStatus.ACTIVE },
    { title: t('booking.list.tabs.finished'), value: EBookingStatus.FINISHED },
  ];

  const [tab, setTab] = useState<EBookingStatus>(EBookingStatus.ACTIVE);
  const [date, setDate] = useState<string | null>(dayjs().format());

  const { data: hostData } = useQuery<IHostResponse>(HOST, {
    variables: { id: hostId },
    fetchPolicy: 'cache-only',
  });
  const [loadBookings, { data, loading }] = useLazyQuery<
    IBookingsResponse,
    IBookingsRequest
  >(BOOKINGS, {
    fetchPolicy: 'cache-and-network',
  });

  const debouncedLoadBookings = useMemo(
    () => debounce(loadBookings, 300),
    [loadBookings]
  );

  useEffect(() => {
    if (!date) return;

    debouncedLoadBookings({
      variables: {
        hostIds: [hostId],
        from: dayjs(date).format('YYYY-MM-DD 00:00:00'),
        to: dayjs(date).add(1, 'day').format('YYYY-MM-DD 00:00:00'),
        status: tab,
      },
    });
  }, [hostId, date, tab]);

  return (
    <Modal onClose={onClose} disableOutsideClose className={s.container}>
      <div className={s.header}>
        <H2>{t('host.ctx.bookings.list')}</H2>
        <span className={s.subtitle}>{hostData?.hostOverview.alias ?? ''}</span>
      </div>
      <Tabs options={TABS} onChange={setTab} value={tab} className={s.tabs} />
      <div className={s['date-bookings']}>
        <DatePicker
          date={date}
          setDate={setDate}
          withTime={false}
          title={t('booking.list.date')}
          fullWidth
          lang={lang}
        />
        <div className={s.bookings}>
          <Preloader loading={loading}>
            {data?.getBookings.data.map((booking) => (
              <div
                key={`all-bookings-${booking.id}`}
                className={cn(
                  s.booking,
                  tab === EBookingStatus.FINISHED && s.finished
                )}
              >
                <div className={s.data}>
                  <div className={s.data_header}>
                    <div className={s.time}>
                      {dayjs(booking.from).format('HH:mm')}
                      <span>→</span>
                      {dayjs(booking.to).format('HH:mm')}
                    </div>
                    {booking.byClient && (
                      <Label
                        color="green"
                        text={t('setting.onlineBooking')}
                        rounded
                        iconName="MobilePhoneIcon"
                      />
                    )}
                  </div>
                  <span className={s.client}>
                    {booking.client?.nickname ?? t('client.guest')}
                  </span>
                </div>
                {tab === EBookingStatus.ACTIVE && !booking.byClient && (
                  <Button
                    variant="ghost"
                    onClick={() => editBooking(booking.id)}
                  >
                    <EditIcon color="link-info" size="m" />
                  </Button>
                )}
              </div>
            ))}
          </Preloader>
        </div>
      </div>
    </Modal>
  );
};
